import { Order } from '@/lib/constants';
import { formatCurrency, formatTime, formatOrderId } from '@/lib/format';
import { CheckCircle2, Clock, MapPin, Package, User, MessageSquare } from 'lucide-react';

interface ActiveOrderCardProps {
  order: Order;
  onUpdateStatus: (status: 'preparing' | 'ready_for_pickup') => void;
  isUpdatingStatus?: boolean;
}

export default function ActiveOrderCard({
  order,
  onUpdateStatus,
  isUpdatingStatus = false,
}: ActiveOrderCardProps) {
  const getStatusStep = (status: string) => {
    switch (status) {
      case 'accepted':
        return 1;
      case 'preparing':
        return 2;
      case 'ready_for_pickup':
        return 3;
      default:
        return 0;
    }
  };

  const currentStep = getStatusStep(order.status);

  const steps = [
    { status: 'accepted', label: 'Accepted', icon: CheckCircle2 },
    { status: 'preparing', label: 'Preparing', icon: Clock },
    { status: 'ready_for_pickup', label: 'Ready for Pickup', icon: Package },
  ];

  const getNextAction = () => {
    switch (order.status) {
      case 'accepted':
        return {
          label: 'Start Preparing',
          action: () => onUpdateStatus('preparing'),
        };
      case 'preparing':
        return {
          label: 'Mark Ready for Pickup',
          action: () => onUpdateStatus('ready_for_pickup'),
        };
      default:
        return null;
    }
  };

  const nextAction = getNextAction();

  return (
    <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            {formatOrderId(order.id)}
          </h3>
          <p className="text-sm text-muted-foreground">
            {formatTime(order.createdAt)}
          </p>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
          ACTIVE
        </span>
      </div>

      {/* Status Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between relative">
          {/* Progress Line */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-border">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{
                width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
              }}
            />
          </div>

          {/* Steps */}
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const stepNumber = index + 1;
            const isComplete = stepNumber <= currentStep;
            const isCurrent = stepNumber === currentStep;

            return (
              <div key={step.status} className="flex flex-col items-center relative z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    isComplete
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'bg-background border-border text-muted-foreground'
                  } ${isCurrent ? 'ring-4 ring-primary/20' : ''}`}
                >
                  <StepIcon className="w-5 h-5" />
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${
                    isComplete ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Customer Info */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-foreground">
            {order.customerName || 'Customer #' + (order.customerId?.slice(0, 8) || 'N/A')}
          </span>
        </div>

        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
          <p className="text-sm text-foreground">
            {order.deliveryAddress || 'No address provided'}
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {order.items?.length || 0} item(s)
            </span>
          </div>
          {order.items && order.items.length > 0 && (
            <div className="ml-6 space-y-1">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-foreground">
                    {item.name || 'Item'}{' '}
                    {(item as any).weightKg ? (
                      <span className="text-muted-foreground">
                        ({item.quantity > 0 ? item.quantity.toFixed(3).replace(/\.?0+$/, '') : item.quantity} kg)
                      </span>
                    ) : (
                      <span className="text-muted-foreground">×{item.quantity}</span>
                    )}
                  </span>
                  <span className="text-muted-foreground">
                    {formatCurrency(item.price * item.quantity)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Buyer Notes (Sprint 27) */}
      {order.notes && (
        <div className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-700 mb-1">Customer Notes</p>
              <p className="text-sm text-blue-800">{order.notes}</p>
            </div>
          </div>
        </div>
      )}

      {/* Total */}
      <div className="border-t border-border pt-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-muted-foreground">Total</span>
          <span className="text-xl font-bold text-foreground">
            {formatCurrency(order.totalAmount)}
          </span>
        </div>
      </div>

      {/* Next Action Button */}
      {nextAction && (
        <button
          onClick={nextAction.action}
          disabled={isUpdatingStatus}
          className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isUpdatingStatus ? 'Updating...' : nextAction.label}
        </button>
      )}

      {/* Ready for Pickup Message (Sprint 27: Enhanced) */}
      {order.status === 'ready_for_pickup' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-700" />
            <p className="font-medium text-green-700">Ready for Pickup</p>
          </div>
          {order.ready_at && (
            <p className="text-xs text-green-600 mb-2">
              Marked ready at {formatTime(order.ready_at)}
            </p>
          )}
          <p className="text-sm text-green-700">
            Prepare items and place in pickup area. Rider will collect shortly.
          </p>
        </div>
      )}
    </div>
  );
}

